import _ from "lodash";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { AppCard } from "@lib/components/AppComponents";
import { useIntl } from "react-intl";
import { ActionButton } from "../../Buttons";
import { Empty, Skeleton, Typography } from "antd";
import { UseQueryProps } from "@lib/types";
import { XAICallLogsDashboardResponse } from "@lib/types/xAI";
import { useMemo } from "react";
import { useTheme } from "styled-components";
import { randomColor } from "@lib/helpers/ColorHelper";
import { StyledFlex } from "../../Table/index.styled";

type Props = {
  query?: UseQueryProps<XAICallLogsDashboardResponse>;
  extra?: string | JSX.Element;
  navigate?: () => void;
  loading?: boolean;
  refetch?: () => void;
};

const TftSalesCallsStatusChart = (props: Props) => {
  const { query, extra, navigate, loading, refetch } = props;
  const { mode } = useTheme();
  const { data, isLoading, isFetching } = query || {};
  const { messages } = useIntl();

  const statusData = useMemo(() => {
    const groupByCallStatusCount = data?.data?.groupByCallStatusCount || [];
    return groupByCallStatusCount
      .map((item) => ({
        status: item.call_status,
        count: item.count,
      }))
      .sort((a, b) => b.count - a.count); // Sort descending by count
  }, [data]);

  const statuses = useMemo(
    () => statusData.map((item) => item.status),
    [statusData],
  );

  const labelText = (status: string) => {
    const item = statusData.find((s) => s.status === status);
    return `${status}: ${item?.count || 0}`;
  };

  const chartSeries: ApexOptions["series"] = useMemo(() => {
    const series: ApexOptions["series"] = statusData.map((item) => item.count);
    return series;
  }, [statusData]);

  const chartOptions = useMemo(() => {
    return {
      theme: {
        mode: mode,
      },
      chart: {
        type: "pie",
        height: 400,
        width: 400,
      },
      labels: statuses,
      colors: statuses.map((v, i) => {
        return randomColor(i);
      }),
      tooltip: {
        y: {
          formatter: (val: number, opt: any) => {
            return labelText(statuses[opt.dataPointIndex]);
          },
        },
        shared: true,
        intersect: false,
      },
      legend: {
        formatter: (val: string) => {
          return labelText(val);
        },
      },
    } as ApexOptions;
  }, [mode, statuses, statusData]);

  return (
    <AppCard
      title={messages["sidebar.tftSalesCalls"] as string}
      extra={
        <StyledFlex style={{ gap: 10 }}>
          {extra}
          <>
            {navigate && (
              <Typography.Link onClick={() => navigate()}>
                {messages["common.viewAll"] as string}
              </Typography.Link>
            )}
          </>
          {refetch && (
            <ActionButton
              type="refresh"
              size="small"
              onClick={() => refetch()}
              disabled={loading}
              loading={loading}
            />
          )}
        </StyledFlex>
      }
    >
      {isLoading || isFetching ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <>
          {statusData.length === 0 ? (
            <div style={{ padding: "20px", textAlign: "center" }}>
              <Empty />
            </div>
          ) : (
            <Chart
              options={chartOptions}
              series={chartSeries}
              type={chartOptions.chart?.type}
              height={chartOptions.chart?.height || 400}
            />
          )}
        </>
      )}
    </AppCard>
  );
};

export default TftSalesCallsStatusChart;
